import { useState, useEffect } from 'react';

interface ScheduleProps {
  userProfile: any;
}

export default function Schedule({ userProfile }: ScheduleProps) {
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [schedule, setSchedule] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const daysOfWeek = ['Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7', 'Chủ nhật'];
  const timeSlots = [
    '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', 
    '14:00', '15:00', '16:00', '17:00', '18:00', '19:00'
  ];

  useEffect(() => {
    fetchSchedule();
  }, [currentWeek, userProfile]);

  const parseSchedule = (scheduleText: string) => {
    const scheduleItems: any[] = [];
    
    if (!scheduleText) return scheduleItems;

    try {
      const beforeParenthesis = scheduleText.split('(')[0];
      const daysMatch = beforeParenthesis.match(/Thứ\s*([0-9,\s]+)/i);
      const timeMatch = scheduleText.match(/\(?\s*(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})\s*\)?/);
      
      if (daysMatch && timeMatch) {
        const days = daysMatch[1]
          .split(',')
          .map(d => d.trim())
          .filter(d => d !== '')
          .map(d => parseInt(d));
        
        const startTime = timeMatch[1];
        const endTime = timeMatch[2];
        
        days.forEach(day => {
          scheduleItems.push({
            day: day,
            start_time: startTime,
            end_time: endTime
          });
        });
      }
    } catch (error) {
      console.error('Error parsing schedule:', error);
    }

    return scheduleItems;
  };

  const fetchSchedule = async () => {
    setLoading(true);
    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

      // Get student's enrollments
      const enrollmentsResponse = await fetch(`${supabaseUrl}/functions/v1/get-enrollments`, {
        headers: {
          'Authorization': `Bearer ${supabaseKey}`,
          'Content-Type': 'application/json'
        }
      });

      if (!enrollmentsResponse.ok) {
        throw new Error('Failed to fetch enrollments');
      }

      const allEnrollments = await enrollmentsResponse.json();
      const studentEnrollments = allEnrollments.filter((e: any) => 
        e.student_id === userProfile.id
      );

      // Get all courses
      const coursesResponse = await fetch(`${supabaseUrl}/functions/v1/get-courses`, {
        headers: {
          'Authorization': `Bearer ${supabaseKey}`,
          'Content-Type': 'application/json'
        }
      });

      if (!coursesResponse.ok) {
        throw new Error('Failed to fetch courses');
      }

      const allCourses = await coursesResponse.json();

      // Build schedule data
      const scheduleData: any[] = [];
      const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 'bg-pink-500', 'bg-indigo-500'];
      
      studentEnrollments.forEach((enrollment: any, index: number) => {
        const course = allCourses.find((c: any) => c.id === enrollment.course_id);
        
        if (course) {
          const scheduleItems = parseSchedule(course.schedule || '');
          
          scheduleItems.forEach((item, idx) => {
            scheduleData.push({
              id: `${course.id}_${idx}`,
              course_id: course.id,
              course: course.title,
              teacher: course.teacher_name || 'Giáo viên',
              day: item.day,
              start_time: item.start_time,
              end_time: item.end_time,
              room: 'Phòng học',
              color: colors[index % colors.length],
              type: 'regular'
            });
          });
        }
      });

      setSchedule(scheduleData);
    } catch (error) {
      console.error('Error fetching schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  const getWeekDates = (date: Date) => {
    const week = [];
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
    startOfWeek.setDate(diff);

    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      week.push(day);
    }
    return week;
  };

  const weekDates = getWeekDates(currentWeek);

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentWeek);
    newDate.setDate(currentWeek.getDate() + (direction === 'next' ? 7 : -7));
    setCurrentWeek(newDate);
  };

  const getClassesForTimeSlot = (day: number, time: string) => {
    const dayNumber = day === 6 ? 8 : day + 2;
    
    return schedule.filter(cls => {
      const startHour = parseInt(cls.start_time.split(':')[0]);
      const currentHour = parseInt(time.split(':')[0]);
      const endHour = parseInt(cls.end_time.split(':')[0]);
      
      return cls.day === dayNumber && currentHour >= startHour && currentHour < endHour;
    });
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-2 text-gray-500">
          <i className="ri-loader-4-line animate-spin text-xl"></i>
          <span>Đang tải lịch học...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Lịch học</h1>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigateWeek('prev')}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-lg"></i>
          </button>
          <div className="text-lg font-medium text-gray-900">
            {weekDates[0].toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' })} - {' '}
            {weekDates[6].toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
          </div>
          <button
            onClick={() => navigateWeek('next')}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-line text-lg"></i>
          </button>
        </div>
      </div>

      {/* Schedule Grid */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <div className="min-w-[800px]">
            {/* Header */}
            <div className="grid grid-cols-8 border-b bg-gray-50">
              <div className="p-4 text-sm font-medium text-gray-600">Giờ</div>
              {daysOfWeek.map((day, index) => (
                <div key={day} className="p-4 text-center">
                  <div className="text-sm font-medium text-gray-900">{day}</div>
                  <div className={`text-xs mt-1 ${isToday(weekDates[index]) ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
                    {weekDates[index].getDate()}/{weekDates[index].getMonth() + 1}
                  </div>
                </div>
              ))}
            </div>

            {/* Time slots */}
            {timeSlots.map((time) => (
              <div key={time} className="grid grid-cols-8 border-b hover:bg-gray-50">
                <div className="p-4 text-sm text-gray-600 border-r bg-gray-50">
                  {time}
                </div>
                {daysOfWeek.map((_, dayIndex) => {
                  const classes = getClassesForTimeSlot(dayIndex, time);
                  return (
                    <div key={dayIndex} className="p-2 border-r min-h-[60px] relative">
                      {classes.map((cls) => (
                        <div
                          key={cls.id}
                          className={`${cls.color} text-white p-2 rounded text-xs mb-1`}
                        >
                          <div className="font-medium truncate">{cls.course}</div>
                          <div className="opacity-90">{cls.teacher}</div>
                          <div className="opacity-90">{cls.room}</div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Today's Classes Summary */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Lịch học hôm nay</h2>
        <div className="space-y-3">
          {schedule
            .filter(cls => {
              const today = new Date().getDay();
              const todayAdjusted = today === 0 ? 8 : today + 1;
              return cls.day === todayAdjusted;
            })
            .sort((a, b) => a.start_time.localeCompare(b.start_time))
            .map((cls) => (
              <div key={cls.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className={`w-4 h-4 ${cls.color} rounded`}></div>
                  <div>
                    <div className="font-medium text-gray-900">{cls.course}</div>
                    <div className="text-sm text-gray-600">
                      {cls.start_time} - {cls.end_time} • {cls.teacher} • {cls.room}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          {schedule.filter(cls => {
            const today = new Date().getDay();
            const todayAdjusted = today === 0 ? 8 : today + 1;
            return cls.day === todayAdjusted;
          }).length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <i className="ri-calendar-line text-3xl mb-2"></i>
              <p>Hôm nay bạn không có lịch học</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
